import {
  BaseApiClient,
  BaseUser,
  BaseOrganization,
  SsoConfig,
  AuditLog,
  type PaginationInfo,
  type PermissionLevel,
  type Group,
  type GroupMember,
  type UserPermission,
  type GroupPermission,
  type ProjectUserPermission,
  type ProjectGroupPermission,
  type ExtractionRun,
  type ExtractionRunFile,
  type ExtractionDefinition,
} from '../../shared/src';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.fakto.ai';

export interface Organization extends BaseOrganization {
  org_id_hash: string;
  user_count?: number;
  sso_configs?: SsoConfig[];
}

export interface User extends BaseUser {
  role: 'user' | 'org_admin';
}

export interface Folder {
  id: string;
  organization_id: string;
  parent_id: string | null;
  name: string;
  description: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface FolderWithMeta extends Folder {
  created_by_email: string;
  file_count: number;
  subfolder_count: number;
  /** Root-relative path of the folder's parent ("Q4/Finance", "" for root); present only on project-scoped search results. */
  folder_path?: string | null;
}

export interface FileUpload {
  id: string;
  file_name: string;
  original_name: string;
  file_size: number;
  content_type: string;
  s3_key: string;
  status: string;
  folder_id: string | null;
  description: string | null;
  created_at: string;
  completed_at: string | null;
  uploaded_by_email: string;
  uploaded_by_first_name: string;
  uploaded_by_last_name: string;
  /** Root-relative folder path ("Q4/Finance", "" for root); present only on project-scoped search results. */
  folder_path?: string | null;
}

/** File-browser search scope: the current folder, or the whole project. */
export type UploadSearchScope = 'folder' | 'project';

export interface OrgProject {
  id: string;
  name: string;
  slug: string;
  root_folder_id: string | null;
  created_at: string;
  external_ui_url: string | null;
}

/**
 * Single-payload backing for the Permissions > Users table. Collapses the old
 * 1 + 2N request fan-out (users/groups/projects + per-user groups & permissions)
 * into one call. `userGroups` / `userPermissions` are keyed by user id, with an
 * entry for every user (possibly empty).
 */
export interface PermissionsOverview {
  users: User[];
  groups: Group[];
  projects: OrgProject[];
  userGroups: Record<string, Group[]>;
  userPermissions: Record<string, UserPermission[]>;
}

/** One-call backing for the Permissions > Projects table (per-project user/group permissions). */
export interface ProjectsPermissionsOverview {
  projects: OrgProject[];
  users: User[];
  groups: Group[];
  projectUserPermissions: Record<string, ProjectUserPermission[]>;
  projectGroupPermissions: Record<string, ProjectGroupPermission[]>;
}

/** One-call backing for the Permissions > Groups table (per-group members + permissions). */
export interface GroupsPermissionsOverview {
  groups: Group[];
  users: User[];
  projects: OrgProject[];
  groupMembers: Record<string, GroupMember[]>;
  groupPermissions: Record<string, GroupPermission[]>;
}

/**
 * An extraction as defined and deployed by the data-extraction repository
 * (read-only here) plus the projects a superadmin mapped it to.
 */
export interface OrgExtraction extends ExtractionDefinition {
  project_ids: string[];
  /** Most recent run, null when it never ran. */
  latest_run: ExtractionRun | null;
}

export type AdminRole = 'super_admin' | 'admin_editor' | 'admin_viewer';

export interface PlatformAdmin {
  id: string;
  email: string;
  name: string | null;
  role: AdminRole;
  status: 'active' | 'invited' | 'suspended';
  last_login_at: string | null;
  created_at: string;
}

export type { SsoConfig, AuditLog };

export type AuditLogSortColumn = 'time' | 'action' | 'organization' | 'user' | 'ip_address';

/** Filters are ANDed; see lambda/admin-api/handlers/audit.ts for their semantics. */
export interface AuditLogQuery {
  page?: number;
  limit?: number;
  actions?: string[];
  action_search?: string;
  /** Organization ids; 'none' selects platform-level rows. */
  organization_ids?: string[];
  user_email?: string;
  user_email_exclude?: string;
  ip_search?: string;
  /** Inclusive days, YYYY-MM-DD, in `tz` (IANA name; the API defaults to UTC). */
  date_from?: string;
  date_to?: string;
  tz?: string;
  sort_by?: AuditLogSortColumn;
  sort_order?: 'asc' | 'desc';
}

class AdminApiClient extends BaseApiClient {
  private isLoggingIn = false;

  constructor() {
    super(API_URL, 'admin_token');
  }

  protected onUnauthorized(): void {
    // Don't redirect during login attempts - let the error be shown
    if (this.isLoggingIn) {
      return;
    }
    // Expired/invalid session: send the admin to sign-in. (Admin tokens have no refresh
    // flow, so this is the terminal path on expiry.) Routed through the reload gate, so the
    // reload waits for a folder download to finish rather than killing it mid-run.
    this.redirectToSignIn('session_expired');
  }

  // Auth
  async login(email: string, password: string): Promise<{ token: string; admin: { id: string; email: string; name: string; role: string } }> {
    this.isLoggingIn = true;
    try {
      const result = await this.request<{ token: string; admin: { id: string; email: string; name: string; role: string } }>(
        'POST',
        '/admin/login',
        { email, password }
      );
      this.setToken(result.token);
      return result;
    } finally {
      this.isLoggingIn = false;
    }
  }

  logout() {
    this.setToken(null);
  }

  // Set password for invited admin (unauthenticated endpoint)
  async setAdminPassword(token: string, password: string): Promise<{ token: string; admin: { id: string; email: string; name: string; role: string } }> {
    this.isLoggingIn = true;
    try {
      const result = await this.request<{ token: string; admin: { id: string; email: string; name: string; role: string } }>(
        'POST',
        '/admin/set-password',
        { token, password }
      );
      this.setToken(result.token);
      return result;
    } finally {
      this.isLoggingIn = false;
    }
  }

  // Organizations
  async listOrganizations(params?: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  }): Promise<{ organizations: Organization[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.status) searchParams.set('status', params.status);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    return this.request('GET', `/admin/organizations${query ? `?${query}` : ''}`);
  }

  async getOrganization(id: string): Promise<Organization> {
    return this.request('GET', `/admin/organizations/${id}`);
  }

  async createOrganization(data: { name: string; slug: string }): Promise<Organization> {
    return this.request('POST', '/admin/organizations', data);
  }

  async updateOrganization(id: string, data: Partial<Organization>): Promise<Organization> {
    return this.request('PUT', `/admin/organizations/${id}`, data);
  }

  async deleteOrganization(id: string): Promise<void> {
    return this.request('DELETE', `/admin/organizations/${id}`);
  }

  // Users
  async listOrgUsers(orgId: string): Promise<{ users: User[] }> {
    return this.request('GET', `/admin/organizations/${orgId}/users`);
  }

  async updateUserRole(orgId: string, userId: string, role: 'user' | 'org_admin'): Promise<{ user: User; message: string }> {
    return this.request('PUT', `/admin/organizations/${orgId}/users/${userId}`, { role });
  }

  async inviteUser(orgId: string, data: {
    email: string;
    firstName: string;
    lastName: string;
    role: 'user' | 'org_admin';
  }): Promise<{ user: User; message: string }> {
    return this.request('POST', `/admin/organizations/${orgId}/users`, data);
  }

  async deleteUser(orgId: string, userId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/admin/organizations/${orgId}/users/${userId}`);
  }

  async resendInvitation(orgId: string, userId: string): Promise<{ message: string }> {
    return this.request('POST', `/admin/organizations/${orgId}/users/${userId}/resend-invitation`, {});
  }

  // Auth Method
  async updateAuthMethod(orgId: string, authMethod: 'sso' | 'password'): Promise<{ organization: Organization; message: string }> {
    return this.request('PUT', `/admin/organizations/${orgId}/auth-method`, { authMethod });
  }

  // SSO
  async configureSso(orgId: string, config: {
    providerType: 'saml' | 'oidc';
    providerName?: string;
    isEnabled?: boolean;
    isPrimary?: boolean;
    samlEntityId?: string;
    samlSsoUrl?: string;
    samlSloUrl?: string;
    samlCertificate?: string;
    samlNameIdFormat?: string;
    oidcIssuerUrl?: string;
    oidcClientId?: string;
    oidcClientSecret?: string;
    oidcScopes?: string;
    attributeMappings?: Record<string, string>;
  }): Promise<SsoConfig> {
    return this.request('POST', `/admin/organizations/${orgId}/sso`, config);
  }

  // Bucket
  async createBucket(orgId: string): Promise<{ bucket: { bucket_name: string }; message: string }> {
    return this.request('POST', `/admin/organizations/${orgId}/bucket`, {});
  }

  // Organization Status Management
  async pauseOrganization(id: string): Promise<{ message: string; organization: { id: string; status: string } }> {
    return this.request('POST', `/admin/organizations/${id}/pause`, {});
  }

  async reactivateOrganization(id: string): Promise<{ message: string; organization: { id: string; status: string } }> {
    return this.request('POST', `/admin/organizations/${id}/reactivate`, {});
  }

  async hardDeleteOrganization(id: string): Promise<{ message: string }> {
    return this.request('POST', `/admin/organizations/${id}/delete`, {});
  }

  // Audit Logs
  async listAuditLogs(params?: AuditLogQuery): Promise<{
    logs: AuditLog[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', params.page.toString());
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.actions?.length) searchParams.set('action', params.actions.join(','));
    if (params?.action_search) searchParams.set('action_search', params.action_search);
    if (params?.organization_ids?.length) searchParams.set('organization_id', params.organization_ids.join(','));
    if (params?.user_email) searchParams.set('user_email', params.user_email);
    if (params?.user_email_exclude) searchParams.set('user_email_exclude', params.user_email_exclude);
    if (params?.ip_search) searchParams.set('ip_search', params.ip_search);
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    if (params?.date_to) searchParams.set('date_to', params.date_to);
    if (params?.tz && (params.date_from || params.date_to)) searchParams.set('tz', params.tz);
    if (params?.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params?.sort_order) searchParams.set('sort_order', params.sort_order);

    const query = searchParams.toString();
    return this.request('GET', `/admin/audit-logs${query ? `?${query}` : ''}`);
  }

  async getAuditLogFilters(): Promise<{ actions: string[] }> {
    return this.request('GET', '/admin/audit-logs/filters');
  }

  // Projects
  async listOrgProjects(orgId: string): Promise<{ projects: OrgProject[] }> {
    return this.request('GET', `/admin/organizations/${orgId}/projects`);
  }

  async updateOrgProject(
    orgId: string,
    projectId: string,
    body: { external_ui_url: string | null },
  ): Promise<{ project: OrgProject }> {
    return this.request(
      'PATCH',
      `/admin/organizations/${orgId}/projects/${projectId}`,
      body,
    );
  }

  // ── Permissions tab (org-scoped; super_admin / admin_editor only) ──
  // Mirror the user-app's Admin → Permissions section, but with the org as a
  // path param. listOrgProjects / listOrgUsers above are reused for listings.

  async createOrgProject(orgId: string, name: string, slug: string): Promise<OrgProject> {
    const response = await this.request<{ project: OrgProject }>('POST', `/admin/organizations/${orgId}/projects`, { name, slug });
    return response.project;
  }

  async updateOrgProjectName(orgId: string, projectId: string, name: string): Promise<OrgProject> {
    const response = await this.request<{ project: OrgProject }>('PATCH', `/admin/organizations/${orgId}/projects/${projectId}`, { name });
    return response.project;
  }

  async deleteOrgProject(orgId: string, projectId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/admin/organizations/${orgId}/projects/${projectId}`);
  }

  async listOrgGroups(orgId: string): Promise<Group[]> {
    const response = await this.request<{ groups: Group[] }>('GET', `/admin/organizations/${orgId}/groups`);
    return response.groups;
  }

  async createOrgGroup(orgId: string, name: string): Promise<Group> {
    const response = await this.request<{ group: Group }>('POST', `/admin/organizations/${orgId}/groups`, { name });
    return response.group;
  }

  async updateOrgGroup(orgId: string, groupId: string, name: string): Promise<Group> {
    const response = await this.request<{ group: Group }>('PATCH', `/admin/organizations/${orgId}/groups/${groupId}`, { name });
    return response.group;
  }

  async deleteOrgGroup(orgId: string, groupId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/admin/organizations/${orgId}/groups/${groupId}`);
  }

  async getOrgGroupMembers(orgId: string, groupId: string): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('GET', `/admin/organizations/${orgId}/groups/${groupId}/members`);
    return response.members;
  }

  async setOrgGroupMembers(orgId: string, groupId: string, userIds: string[]): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('PUT', `/admin/organizations/${orgId}/groups/${groupId}/members`, { userIds });
    return response.members;
  }

  // Per-member add/remove — preferred over setOrgGroupMembers for UI toggles so
  // concurrent toggles can't clobber each other (no read-modify-write).
  async addOrgGroupMember(orgId: string, groupId: string, userId: string): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('POST', `/admin/organizations/${orgId}/groups/${groupId}/members/${userId}`, {});
    return response.members;
  }

  async removeOrgGroupMember(orgId: string, groupId: string, userId: string): Promise<GroupMember[]> {
    const response = await this.request<{ members: GroupMember[] }>('DELETE', `/admin/organizations/${orgId}/groups/${groupId}/members/${userId}`);
    return response.members;
  }

  async getOrgUserGroups(orgId: string, userId: string): Promise<Group[]> {
    const response = await this.request<{ groups: Group[] }>('GET', `/admin/organizations/${orgId}/users/${userId}/groups`);
    return response.groups;
  }

  async getOrgUserPermissions(orgId: string, userId: string): Promise<UserPermission[]> {
    const response = await this.request<{ permissions: UserPermission[] }>('GET', `/admin/organizations/${orgId}/permissions/users/${userId}`);
    return response.permissions;
  }

  // One round-trip for the whole Permissions > Users table (users, groups,
  // projects, and each user's groups + permissions). Replaces the per-user
  // getOrgUserGroups / getOrgUserPermissions fan-out on initial load.
  async getPermissionsOverview(orgId: string): Promise<PermissionsOverview> {
    return this.request('GET', `/admin/organizations/${orgId}/permissions/overview`);
  }

  // One round-trip for the whole Permissions > Projects table.
  async getProjectsPermissionsOverview(orgId: string): Promise<ProjectsPermissionsOverview> {
    return this.request('GET', `/admin/organizations/${orgId}/permissions/projects-overview`);
  }

  // One round-trip for the whole Permissions > Groups table.
  async getGroupsPermissionsOverview(orgId: string): Promise<GroupsPermissionsOverview> {
    return this.request('GET', `/admin/organizations/${orgId}/permissions/groups-overview`);
  }

  async setOrgUserPermission(orgId: string, userId: string, projectId: string, permission: PermissionLevel | null): Promise<{ message: string }> {
    return this.request('PUT', `/admin/organizations/${orgId}/permissions/users/${userId}/projects/${projectId}`, { permission });
  }

  async getOrgGroupPermissions(orgId: string, groupId: string): Promise<GroupPermission[]> {
    const response = await this.request<{ permissions: GroupPermission[] }>('GET', `/admin/organizations/${orgId}/permissions/groups/${groupId}`);
    return response.permissions;
  }

  async setOrgGroupPermission(orgId: string, groupId: string, projectId: string, permission: PermissionLevel | null): Promise<{ message: string }> {
    return this.request('PUT', `/admin/organizations/${orgId}/permissions/groups/${groupId}/projects/${projectId}`, { permission });
  }

  async getOrgProjectPermissions(orgId: string, projectId: string): Promise<{ userPermissions: ProjectUserPermission[]; groupPermissions: ProjectGroupPermission[] }> {
    return this.request('GET', `/admin/organizations/${orgId}/permissions/projects/${projectId}`);
  }

  // File Browser (read-only view)
  async listOrgFolders(
    orgId: string,
    parentId?: string | null,
    opts?: { q?: string; scope?: UploadSearchScope; projectId?: string },
  ): Promise<{ folders: FolderWithMeta[]; truncated: boolean }> {
    const params = new URLSearchParams();
    if (parentId && parentId !== 'root') params.set('parentId', parentId);
    if (opts?.q) params.set('q', opts.q);
    if (opts?.scope) params.set('scope', opts.scope);
    if (opts?.scope === 'project' && opts.projectId) params.set('projectId', opts.projectId);
    const qs = params.toString();
    const res = await this.request<{ folders: FolderWithMeta[]; truncated?: boolean }>(
      'GET',
      `/admin/organizations/${orgId}/folders${qs ? `?${qs}` : ''}`,
    );
    return { folders: res.folders, truncated: res.truncated ?? false };
  }

  async getOrgFolder(orgId: string, folderId: string): Promise<{ folder: Folder; path: Folder[] }> {
    return this.request('GET', `/admin/organizations/${orgId}/folders/${folderId}`);
  }

  // Move a folder (with its whole subtree) to another project in the same org.
  // The folder lands at the target project's root. The move (DB flip) is atomic
  // and returns immediately; `total`/`relocated` seed the re-key progress bar.
  // super_admin / admin_editor only.
  async moveOrgFolder(
    orgId: string,
    folderId: string,
    targetProjectId: string,
  ): Promise<{ folder: Folder; total: number; relocated: number }> {
    return this.request('POST', `/admin/organizations/${orgId}/folders/${folderId}/move`, { targetProjectId });
  }

  // Re-key the next chunk of a moved folder's files to its new project slug.
  // Call in a loop until `done`, accumulating `moved` into the progress bar.
  // A zero-progress batch returns a (retryable) error rather than `done`.
  async rekeyFolderBatch(
    orgId: string,
    folderId: string,
  ): Promise<{ done: boolean; processed: number; moved: number; failed: number }> {
    return this.request('POST', `/admin/organizations/${orgId}/folders/${folderId}/rekey-batch`, {});
  }

  async listOrgUploads(
    orgId: string,
    folderId?: string | null,
    opts?: { page?: number; limit?: number; q?: string; scope?: UploadSearchScope; projectId?: string },
  ): Promise<{ uploads: FileUpload[]; pagination: PaginationInfo }> {
    const params = new URLSearchParams();
    if (folderId && folderId !== 'root') params.set('folderId', folderId);
    if (opts?.page) params.set('page', String(opts.page));
    if (opts?.limit) params.set('limit', String(opts.limit));
    if (opts?.q) params.set('q', opts.q);
    if (opts?.scope) params.set('scope', opts.scope);
    // Project scope needs the project to search within (admin lists by org+folder).
    if (opts?.scope === 'project' && opts.projectId) params.set('projectId', opts.projectId);
    const qs = params.toString();
    return this.request('GET', `/admin/organizations/${orgId}/uploads${qs ? `?${qs}` : ''}`);
  }

  async getFileDownloadUrl(
    orgId: string,
    fileId: string
  ): Promise<{ url: string; filename: string; contentType: string; size: number; expiresAt: number }> {
    return this.request('GET', `/admin/organizations/${orgId}/uploads/${fileId}/download-url`);
  }

  async getFolderDownloadManifest(
    orgId: string,
    folderId: string,
    opts?: { offset?: number; limit?: number }
  ): Promise<{
    folderName: string;
    files: Array<{ id: string; relativePath: string; url: string; size: number; contentType: string }>;
    total: number;
    offset: number;
    limit: number;
    expiresAt: number;
  }> {
    const params = new URLSearchParams();
    if (opts?.offset != null) params.set('offset', String(opts.offset));
    if (opts?.limit != null) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return this.request(
      'GET',
      `/admin/organizations/${orgId}/folders/${folderId}/download-manifest${qs ? `?${qs}` : ''}`
    );
  }

  // Extractions (defined in data-extraction, read-only here) + project mapping + runs
  async listOrgExtractions(orgId: string): Promise<{ extractions: OrgExtraction[]; orphaned_keys: string[]; ledger_configured: boolean }> {
    return this.request('GET', `/admin/organizations/${orgId}/extractions`);
  }

  async setOrgExtractionProjects(orgId: string, extractionKey: string, projectIds: string[]): Promise<{ extraction: OrgExtraction }> {
    return this.request('PUT', `/admin/organizations/${orgId}/extractions/${encodeURIComponent(extractionKey)}/projects`, { project_ids: projectIds });
  }

  /** Starts a manual run now (editors and super admins). The run settles asynchronously. */
  async triggerOrgExtractionRun(orgId: string, extractionKey: string): Promise<{ run: { run_id: string; status: string } }> {
    return this.request('POST', `/admin/organizations/${orgId}/extractions/${encodeURIComponent(extractionKey)}/runs`);
  }

  async listOrgExtractionRuns(
    orgId: string,
    extractionKey: string,
    limit = 50,
  ): Promise<{ extraction: ExtractionDefinition; runs: ExtractionRun[]; ledger_configured: boolean }> {
    return this.request('GET', `/admin/organizations/${orgId}/extractions/${encodeURIComponent(extractionKey)}/runs?limit=${limit}`);
  }

  /** One page of a run's file rows; pass `next_cursor` back until it is null. */
  async getOrgExtractionRun(
    orgId: string,
    extractionKey: string,
    runId: string,
    cursor?: string,
  ): Promise<{ extraction: ExtractionDefinition; run: ExtractionRun; files: ExtractionRunFile[]; next_cursor: string | null }> {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    const qs = params.toString();
    return this.request(
      'GET',
      `/admin/organizations/${orgId}/extractions/${encodeURIComponent(extractionKey)}/runs/${encodeURIComponent(runId)}${qs ? `?${qs}` : ''}`,
    );
  }

  // Platform Admin Management
  async listAdmins(): Promise<{ admins: PlatformAdmin[] }> {
    return this.request('GET', '/admin/admins');
  }

  async inviteAdmin(data: {
    email: string;
    name?: string;
    role: AdminRole;
  }): Promise<{ admin: PlatformAdmin; message: string }> {
    return this.request('POST', '/admin/admins', data);
  }

  async updateAdmin(
    adminId: string,
    data: { name?: string; role?: AdminRole; status?: 'active' | 'suspended' }
  ): Promise<{ admin: PlatformAdmin; message: string }> {
    return this.request('PUT', `/admin/admins/${adminId}`, data);
  }

  async deleteAdmin(adminId: string): Promise<{ message: string }> {
    return this.request('DELETE', `/admin/admins/${adminId}`);
  }

  async resendAdminInvitation(adminId: string): Promise<{ message: string }> {
    return this.request('POST', `/admin/admins/${adminId}/resend-invitation`, {});
  }

  // Current Admin Profile
  async getCurrentAdmin(): Promise<{ admin: PlatformAdmin }> {
    return this.request('GET', '/admin/me');
  }

  async updateCurrentAdmin(data: {
    name?: string;
    currentPassword?: string;
    newPassword?: string;
  }): Promise<{ admin: PlatformAdmin; message: string }> {
    return this.request('PUT', '/admin/me', data);
  }
}

export const api = new AdminApiClient();

// Re-export shared RBAC types for convenience in permission components
export type {
  PermissionLevel,
  Group,
  GroupMember,
  UserPermission,
  GroupPermission,
  ProjectUserPermission,
  ProjectGroupPermission,
} from '../../shared/src';
